import React, { useCallback, useMemo, useState } from 'react';
import { Card, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { MultiValue } from 'react-select';
import ServerError from '../../../../../../components/ServerError';
import { useEmployeesQuery } from '../../../../__generated__/graphql';
import { FlairMultiSelectDropdown } from '../../../../../../atomic/molecules/FlairDropdown/FlairMultiSelectDropdown/FlairMultiSelectDropdown';
import { FilterSelectOption } from '../../../../../../atomic/molecules/FlairDropdown/types';
import {
  CardHeaderFilter,
  PageSize,
} from '../../../components/CardHeaderFilter';
import EmployeesTable from './EmployeesTable';
import Loading from './Loading';
import {
  useTableContentFiltration,
  useTableContentPageSize,
} from '../../../components/useTableContent';
import {
  mapManagerReportsType,
  useManagerReportsMode,
} from '../../../../hooks/useManagerReportsMode';
import { EmployeeStatus, getEmployeeStatus } from './employeeStatus';

const INITIAL_STATUSES: EmployeeStatus[] = ['current'];

type CardContentProps = {
  filter: string;
  pageSize: number;
  selectedStatuses: EmployeeStatus[];
};

const CardContent: React.FC<CardContentProps> = ({
  filter,
  pageSize,
  selectedStatuses,
}) => {
  const [reportsMode] = useManagerReportsMode('myteam');
  const showFuture = selectedStatuses.includes('future');
  const showPast = selectedStatuses.includes('past');
  const { data, loading, error } = useEmployeesQuery({
    variables: {
      managerReportsType: mapManagerReportsType(reportsMode),
      showFutureEmployees: showFuture ? true : null,
      showPastEmployees: showPast ? true : null,
    },
  });

  if (loading) {
    return <Loading pageSize={pageSize} />;
  }

  if (error || data === undefined) {
    return <ServerError />;
  }

  const filtered = data.manager.employees.filter((e) =>
    selectedStatuses.includes(getEmployeeStatus(e)),
  );

  return <EmployeesTable data={filtered} filter={filter} pageSize={pageSize} />;
};

const INITIAL_PAGE_SIZE: PageSize = 50;

const EmployeesList: React.FC = () => {
  const { t } = useTranslation();
  const [filter, onFilterChange] = useTableContentFiltration();
  const [pageSize, onPageSizeChange] =
    useTableContentPageSize(INITIAL_PAGE_SIZE);
  const [selectedStatuses, setSelectedStatuses] =
    useState<EmployeeStatus[]>(INITIAL_STATUSES);

  const statusOptions: FilterSelectOption[] = useMemo(
    () => [
      {
        value: 'current',
        label: t('employees.all.table.statusFilter.current'),
      },
      { value: 'future', label: t('employees.all.table.statusFilter.future') },
      { value: 'past', label: t('employees.all.table.statusFilter.past') },
    ],
    [t],
  );

  const handleStatusChange = useCallback(
    (selected: MultiValue<FilterSelectOption>) => {
      setSelectedStatuses(selected.map((o) => o.value as EmployeeStatus));
    },
    [],
  );

  return (
    <Card>
      <CardHeaderFilter
        onFilterChange={onFilterChange}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
        placeholder={t('employees.all.table.filterPlaceholder')}>
        <Col className="col-auto">
          <FlairMultiSelectDropdown
            options={statusOptions}
            selectedOptions={selectedStatuses}
            onChange={handleStatusChange}
            placeholder={t('employees.all.table.statusFilter.placeholder')}
            isSearchable={false}
          />
        </Col>
      </CardHeaderFilter>
      <CardContent
        filter={filter}
        pageSize={pageSize}
        selectedStatuses={selectedStatuses}
      />
    </Card>
  );
};

export default EmployeesList;
